import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaCalculator, FaFileSignature, FaCheckCircle, FaInfoCircle, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import librosContablesService from '../../services/librosContablesService';
import '../../styles/pages/RRHH/FiniquitosPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Causales de término de contrato según Código del Trabajo de Chile
 */
const CAUSALES_TERMINO = [
    { value: 'ART_159_1', label: 'Art. 159 N°1 - Mutuo acuerdo de las partes' },
    { value: 'ART_159_2', label: 'Art. 159 N°2 - Renuncia del trabajador' },
    { value: 'ART_159_3', label: 'Art. 159 N°3 - Muerte del trabajador' },
    { value: 'ART_159_4', label: 'Art. 159 N°4 - Vencimiento del plazo convenido' },
    { value: 'ART_159_5', label: 'Art. 159 N°5 - Conclusión del trabajo o servicio' },
    { value: 'ART_159_6', label: 'Art. 159 N°6 - Caso fortuito o fuerza mayor' },
    { value: 'ART_160_1', label: 'Art. 160 N°1 - Falta de probidad, vías de hecho, injurias o conducta inmoral' },
    { value: 'ART_160_2', label: 'Art. 160 N°2 - Negociaciones prohibidas' },
    { value: 'ART_160_3', label: 'Art. 160 N°3 - No concurrencia sin causa justificada' },
    { value: 'ART_160_4', label: 'Art. 160 N°4 - Abandono del trabajo' },
    { value: 'ART_160_5', label: 'Art. 160 N°5 - Actos, omisiones o imprudencias temerarias' },
    { value: 'ART_160_6', label: 'Art. 160 N°6 - Perjuicio material causado intencionalmente' },
    { value: 'ART_160_7', label: 'Art. 160 N°7 - Incumplimiento grave de las obligaciones' },
    { value: 'ART_161_1', label: 'Art. 161 inc. 1° - Necesidades de la empresa' },
    { value: 'ART_161_2', label: 'Art. 161 inc. 2° - Desahucio del empleador' }
];

/**
 * Fecha de hoy en formato YYYY-MM-DD según la hora local (toISOString usa UTC y de
 * noche en Chile ya devuelve el día siguiente).
 */
const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** 'YYYY-MM-DD' → 'DD-MM-YYYY', leyendo el texto para no correr la fecha por zona horaria */
const formatearFechaISO = (fecha) => (fecha ? String(fecha).slice(0, 10).split('-').reverse().join('-') : '');

/** Días con dos decimales y coma: 7.2083 → "7,21" */
const diasTexto = (dias) => (Number(dias) || 0).toFixed(2).replace('.', ',');

/** Año y mes de una fecha 'YYYY-MM-DD' (o de su JSON), leyendo el texto */
const anioMes = (fecha) => String(fecha).slice(0, 7).split('-').map(Number);

/**
 * Página de Finiquitos
 * Gestión de liquidaciones finales por término de contrato
 */
const FiniquitosPage = ({ token, id_company }) => {
    const [finiquitos, setFiniquitos] = useState([]);
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado (para formularios)
    const [loading, setLoading] = useState(true);
    const [showCalcModal, setShowCalcModal] = useState(false);
    const [procesando, setProcesando] = useState(false); // evita un doble clic que crearía dos finiquitos

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');

    // Meses cerrados por año: { [anio]: { [mes]: true si está cerrado } }. Un mes
    // cerrado no admite asientos nuevos ni que se borren los suyos.
    const [cierres, setCierres] = useState({});

    // Formulario cálculo automático mejorado
    const [calcData, setCalcData] = useState({
        id_colaborador: '',
        fecha_aviso: '',
        fecha_fin_contrato: '',
        fecha_pago: hoyISO(),
        fecha_redaccion: hoyISO(),
        causal_termino: '',
        dias_feriado_tomados: '',
        anios_empleadores_anteriores: ''
    });

    const fetchColaboradores = useCallback(async () => {
        try {
            // Cargar colaboradores disponibles (sin finiquito pagado) para formularios
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    }, [id_company, token]);

    const fetchFiniquitos = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company, estado: filtroEstado };

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/finiquitos`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setFiniquitos(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar finiquitos:', error);
            Swal.fire('Error', 'No se pudieron cargar los finiquitos', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, filtroEstado, token]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchFiniquitos();
    }, [fetchFiniquitos]);

    // Estado de cierre de los años en que caen los asientos de la tabla (devengo y pago)
    useEffect(() => {
        const anios = new Set();
        finiquitos.forEach(f => [f.fecha_redaccion, f.fecha_pago].forEach(fecha => {
            if (fecha) anios.add(anioMes(fecha)[0]);
        }));
        const faltan = [...anios].filter(anio => !(anio in cierres));
        if (faltan.length === 0) return;
        Promise.all(faltan.map(anio => librosContablesService.getEstadoCierre(id_company, anio, token).then(mapa => [anio, mapa])))
            .then(pares => setCierres(prev => ({ ...prev, ...Object.fromEntries(pares) })))
            .catch(err => console.error('Error obteniendo el estado de cierre', err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finiquitos, id_company, token]);

    /** Si el mes de la fecha está cerrado, según lo ya cargado */
    const mesCerrado = (fecha) => {
        if (!fecha) return false;
        const [anio, mes] = anioMes(fecha);
        return Boolean(cierres[anio]?.[mes]);
    };

    /** Igual que mesCerrado, pero trae el año si todavía no está cargado */
    const verificarMesCerrado = async (fecha) => {
        const [anio, mes] = anioMes(fecha);
        let mapa = cierres[anio];
        if (!mapa) {
            mapa = await librosContablesService.getEstadoCierre(id_company, anio, token);
            setCierres(prev => ({ ...prev, [anio]: mapa }));
        }
        return Boolean(mapa[mes]);
    };

    // Un solo paso: calcula, guarda el finiquito con su detalle y su asiento de
    // devengo, y genera el PDF. Si algo quedó mal se corrige con Eliminar, que deshace todo.
    const handleCalcular = async (e) => {
        e.preventDefault();
        if (procesando) return;

        try {
            // El devengo se registra con la fecha de redacción: su mes no puede estar cerrado
            if (await verificarMesCerrado(calcData.fecha_redaccion)) {
                const [anio, mes] = anioMes(calcData.fecha_redaccion);
                Swal.fire('Mes cerrado', `El mes ${mes}/${anio} está cerrado: no se puede registrar el devengo del finiquito. Elija otra fecha de redacción.`, 'warning');
                return;
            }

            setProcesando(true);
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/finiquitos/crear-con-calculo`,
                {
                    id_company,
                    id_colaborador: calcData.id_colaborador,
                    fecha_aviso: calcData.fecha_aviso,
                    fecha_termino: calcData.fecha_fin_contrato,
                    fecha_pago: calcData.fecha_pago,
                    fecha_redaccion: calcData.fecha_redaccion,
                    causal_termino: calcData.causal_termino,
                    dias_feriado_tomados: calcData.dias_feriado_tomados || 0,
                    anios_empleadores_anteriores: calcData.anios_empleadores_anteriores || 0
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const calc = response.data.calculo;
                const pdfUrl = response.data.pdf_url;
                const tieneIndemnizacion = calc.detalleIndemnizacion?.tieneDerechoIndemnizacion;
                const tope90UFAplicado = calc.detalleIndemnizacion?.tope90UFAplicado;

                setShowCalcModal(false);
                resetCalcForm();
                fetchFiniquitos();

                Swal.fire({
                    title: `Finiquito N° ${response.data.data.id} creado`,
                    html: `
                        <div style="text-align: left; font-size: 14px;">
                            <div style="background: #f5f5f5; padding: 10px; border-radius: 5px; margin-bottom: 10px;">
                                <p><strong>Causal:</strong> ${calc.datosColaborador?.nombreCausal || ''}</p>
                                <p><strong>Años de servicio:</strong> ${calc.detalleFeriado?.aniosTrabajados?.toFixed(2) || 0}</p>
                            </div>

                            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Feriado proporcional (${diasTexto(calc.detalleFeriado?.diasFeriadoPendientes)} hábiles + ${calc.detalleFeriado?.diasInhabiles || 0} inhábiles = ${diasTexto(calc.detalleFeriado?.diasCorridos)} días${calc.detalleFeriado?.diasProgresivos ? `; incluye ${calc.detalleFeriado.diasProgresivos} día${calc.detalleFeriado.diasProgresivos > 1 ? 's' : ''} progresivo${calc.detalleFeriado.diasProgresivos > 1 ? 's' : ''}` : ''})</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoFeriadoProporcional || 0).toLocaleString('es-CL')}</td>
                                </tr>
                                ${tieneIndemnizacion ? `
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Indemnización (${calc.detalleIndemnizacion?.aniosIndemnizables || 0} años)${tope90UFAplicado ? ' <span style="color: orange;">(Tope 90 UF)</span>' : ''}</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoIndemnizacion || 0).toLocaleString('es-CL')}</td>
                                </tr>` : `
                                <tr style="border-bottom: 1px solid #ddd; color: #999;">
                                    <td style="padding: 5px;">Indemnización${calc.detalleIndemnizacion?.motivo ? ` <span style="font-size: 12px;">(${calc.detalleIndemnizacion.motivo})</span>` : ''}</td>
                                    <td style="text-align: right; padding: 5px;">No aplica</td>
                                </tr>`}
                                ${calc.detalleMesAviso?.aplicaIndemnizacionAviso ? `
                                <tr style="border-bottom: 1px solid #ddd;">
                                    <td style="padding: 5px;">Mes de aviso (${calc.detalleMesAviso?.diasAvisoDados || 0} días dados)</td>
                                    <td style="text-align: right; padding: 5px;">$${parseInt(calc.resumen?.montoMesAviso || 0).toLocaleString('es-CL')}</td>
                                </tr>` : ''}
                            </table>

                            <div style="background: #e8f5e9; padding: 10px; border-radius: 5px; text-align: center;">
                                <strong style="font-size: 16px;">Total Finiquito: $${parseInt(calc.resumen?.montoTotalFiniquito || 0).toLocaleString('es-CL')}</strong>
                            </div>

                            ${calc.indicadoresUsados?.uf ? `
                            <p style="font-size: 11px; color: #666; margin-top: 10px;">
                                UF del ${formatearFechaISO(calc.indicadoresUsados.fechaUF)}: $${calc.indicadoresUsados.uf.toLocaleString('es-CL')} | Tope 90 UF: $${parseInt(calc.indicadoresUsados.tope90UFPesos || 0).toLocaleString('es-CL')}
                            </p>` : ''}
                            ${pdfUrl ? '' : '<p style="font-size: 12px; color: #c62828; margin-top: 10px;">No se pudo generar el PDF.</p>'}
                        </div>
                    `,
                    icon: 'success',
                    width: 560,
                    showCancelButton: Boolean(pdfUrl),
                    confirmButtonText: pdfUrl ? 'Descargar PDF' : 'Cerrar',
                    cancelButtonText: 'Cerrar',
                    confirmButtonColor: '#1976D2'
                }).then((result) => {
                    if (result.isConfirmed && pdfUrl) {
                        window.open(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}/uploads/${pdfUrl}`, '_blank');
                    }
                });
            }
        } catch (error) {
            console.error('Error al crear finiquito:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo crear el finiquito', 'error');
        } finally {
            setProcesando(false);
        }
    };

    // Pide la fecha de pago y registra el pago: estado PAGADO y asiento D FINIQUITOS
    // POR PAGAR / H BANCO con esa fecha. Parte con la fecha de pago del finiquito.
    const handleRegistrarPago = async (finiq) => {
        try {
            const { value: fechaPago } = await Swal.fire({
                title: 'Registrar pago del finiquito',
                html: `
                    <p>${finiq.nombres} ${finiq.apellido_paterno}: <strong>$${parseInt(finiq.monto_total_finiquito || 0).toLocaleString('es-CL')}</strong></p>
                    <p style="font-size: 13px; color: #666;">Se registrará el asiento de pago con esta fecha: FINIQUITOS POR PAGAR al debe y BANCO al haber.</p>
                    <p style="font-size: 13px; color: #666;">El colaborador quedará DESPEDIDO y sus contratos vigentes, TERMINADOS.</p>
                `,
                input: 'date',
                inputLabel: 'Fecha de pago',
                inputValue: finiq.fecha_finiquito ? String(finiq.fecha_finiquito).slice(0, 10) : hoyISO(),
                showCancelButton: true,
                confirmButtonText: 'Registrar pago',
                cancelButtonText: 'Cancelar',
                inputValidator: async (valor) => {
                    if (!valor) return 'Ingrese la fecha de pago';
                    if (await verificarMesCerrado(valor)) {
                        const [anio, mes] = anioMes(valor);
                        return `El mes ${mes}/${anio} está cerrado: elija otra fecha de pago`;
                    }
                    return null;
                }
            });

            if (!fechaPago) return;

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/finiquitos/${finiq.id}/pago`,
                { fecha_pago: fechaPago },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                Swal.fire('Pago registrado', 'El finiquito quedó pagado y se registró su asiento de pago.', 'success');
                fetchFiniquitos();
            }
        } catch (error) {
            console.error('Error al registrar el pago:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo registrar el pago', 'error');
        }
    };

    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar finiquito?',
            text: 'Se eliminarán el finiquito, su cálculo, sus asientos contables (devengo y, si está pagado, el pago) y su PDF. Si estaba pagado, el colaborador vuelve a ACTIVO y sus contratos a VIGENTE. Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d32f2f'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(
                    `${API_BASE_URL}/api/rrhh/finiquitos/${id}`,
                    {
                        params: { id_company },
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                if (response.data.success) {
                    Swal.fire('Eliminado', 'Finiquito eliminado exitosamente', 'success');
                    fetchFiniquitos();
                }
            } catch (error) {
                console.error('Error al eliminar finiquito:', error);
                Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar el finiquito', 'error');
            }
        }
    };

    const resetCalcForm = () => {
        setCalcData({
            id_colaborador: '',
            fecha_aviso: '',
            fecha_fin_contrato: '',
            fecha_pago: hoyISO(),
            fecha_redaccion: hoyISO(),
            causal_termino: '',
            dias_feriado_tomados: '',
            anios_empleadores_anteriores: ''
        });
    };

    return (
        <div className="finiquitos-container">
            <div className="finiquitos-header">
                <h1><FaFileSignature /> Finiquitos</h1>
                <div className="header-buttons">
                    <button className="btn-calcular" onClick={() => { resetCalcForm(); setShowCalcModal(true); }}>
                        <FaCalculator /> Calcular Automático
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="finiquitos-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="PENDIENTE">Pendientes</option>
                        <option value="PAGADO">Pagados</option>
                    </select>
                </div>
            </div>

            {/* Tabla de finiquitos */}
            {loading ? (
                <div className="loading">Cargando finiquitos...</div>
            ) : (
                <div className="finiquitos-table-container">
                    <table className="finiquitos-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Fecha Fin Contrato</th>
                                <th>Causal Término</th>
                                <th>Total</th>
                                <th>Estado</th>
                                <th>PDF</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {finiquitos.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron finiquitos
                                    </td>
                                </tr>
                            ) : (
                                finiquitos.map((finiq) => {
                                    const causal = CAUSALES_TERMINO.find(c => c.value === finiq.causal_termino);
                                    return (
                                        <tr key={finiq.id}>
                                            <td>{finiq.nombres} {finiq.apellido_paterno}</td>
                                            <td>{finiq.fecha_ultimo_dia_trabajado ? new Date(finiq.fecha_ultimo_dia_trabajado).toLocaleDateString('es-CL') : '-'}</td>
                                            <td className="causal-cell" title={causal?.label}>
                                                {causal?.label || finiq.causal_termino || '-'}
                                            </td>
                                            <td className="amount total">${parseInt(finiq.monto_total_finiquito || 0).toLocaleString('es-CL')}</td>
                                            <td>
                                                <span className={`badge badge-${finiq.estado?.toLowerCase() || 'pendiente'}`}>
                                                    {finiq.estado || 'PENDIENTE'}
                                                </span>
                                            </td>
                                            <td className="pdf-cell">
                                                {finiq.pdf_url ? (
                                                    <a
                                                        href={finiq.pdf_url.startsWith('http') ? finiq.pdf_url : `${API_BASE_URL}/uploads/${finiq.pdf_url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-pdf"
                                                        title="Descargar PDF"
                                                    >
                                                        <FaFileSignature />
                                                    </a>
                                                ) : (
                                                    <span style={{ color: '#999' }}>-</span>
                                                )}
                                            </td>
                                            <td className="actions-cell">
                                                <div className="action-buttons">
                                                    {finiq.estado === 'PENDIENTE' && (
                                                        <button
                                                            className="btn-pagar"
                                                            onClick={() => handleRegistrarPago(finiq)}
                                                            title="Marcar como pagado"
                                                        >
                                                            <FaCheckCircle />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn-eliminar"
                                                        onClick={() => handleEliminar(finiq.id)}
                                                        disabled={mesCerrado(finiq.fecha_redaccion) || mesCerrado(finiq.fecha_pago)}
                                                        title={mesCerrado(finiq.fecha_redaccion) || mesCerrado(finiq.fecha_pago)
                                                            ? 'Sus asientos están en un mes cerrado: no se puede eliminar'
                                                            : 'Eliminar finiquito'}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de cálculo automático */}
            {showCalcModal && (
                <div className="modal-overlay" onClick={() => { setShowCalcModal(false); resetCalcForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Calcular Finiquito Automáticamente</h2>
                        <p className="modal-description">
                            El sistema calculará automáticamente vacaciones e indemnización según años de servicio
                        </p>
                        <form onSubmit={handleCalcular}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Colaborador *</label>
                                    <select
                                        value={calcData.id_colaborador}
                                        onChange={(e) => setCalcData({...calcData, id_colaborador: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {colaboradoresDisponibles.map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.nombres} {col.apellido_paterno}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Causal de Término *</label>
                                    <select
                                        value={calcData.causal_termino}
                                        onChange={(e) => setCalcData({...calcData, causal_termino: e.target.value})}
                                        required
                                    >
                                        <option value="">Seleccione causal...</option>
                                        {CAUSALES_TERMINO.map(causal => (
                                            <option key={causal.value} value={causal.value}>
                                                {causal.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Aviso *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_aviso}
                                        onChange={(e) => setCalcData({...calcData, fecha_aviso: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Fin de Contrato *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_fin_contrato}
                                        onChange={(e) => setCalcData({...calcData, fecha_fin_contrato: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Pago *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_pago}
                                        onChange={(e) => setCalcData({...calcData, fecha_pago: e.target.value})}
                                        required
                                    />
                                    <small style={{color: '#666', fontSize: '11px'}}>Fija la UF del tope de 90 UF (último día del mes anterior)</small>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Redacción *</label>
                                    <input
                                        type="date"
                                        value={calcData.fecha_redaccion}
                                        onChange={(e) => setCalcData({...calcData, fecha_redaccion: e.target.value})}
                                        required
                                    />
                                    <small style={{color: '#666', fontSize: '11px'}}>Fecha que lleva el documento del finiquito</small>
                                </div>

                                <div className="form-group">
                                    <label>Días de Vacaciones Tomados</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={calcData.dias_feriado_tomados}
                                        onChange={(e) => setCalcData({...calcData, dias_feriado_tomados: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                                        placeholder="0"
                                    />
                                    <small style={{color: '#666', fontSize: '11px'}}>Días ya utilizados por el trabajador</small>
                                </div>

                                <div className="form-group">
                                    <label>Años con Empleadores Anteriores</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        step="1"
                                        value={calcData.anios_empleadores_anteriores}
                                        onChange={(e) => setCalcData({...calcData, anios_empleadores_anteriores: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                                        placeholder="0"
                                    />
                                    <small style={{color: '#666', fontSize: '11px'}}>Según certificado AFP, máximo 10. Solo para el feriado progresivo</small>
                                </div>
                            </div>

                            <div className="calc-info-box">
                                <FaInfoCircle style={{marginRight: '8px'}} />
                                <span>
                                    El sistema calculará automáticamente según el Código del Trabajo:
                                    feriado proporcional (15 días/año), indemnización con tope de 90 UF,
                                    y mes de aviso si corresponde. Al calcular, el finiquito queda creado
                                    con su PDF y su asiento de devengo; si algo queda mal, elimínelo y vuelva a calcular.
                                </span>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowCalcModal(false); resetCalcForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-calcular" disabled={procesando}>
                                    <FaCalculator /> {procesando ? 'Calculando…' : 'Calcular Finiquito'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default FiniquitosPage;
